import { JIRA_SUPPORT_TICKET_LINK } from 'constants/links'
import DependentInput from 'elements/input/DependentInput'
import { Grid, OsTextFieldVersion, SectionDivider, useComponentVersion } from 'opensolar-ui'
import React from 'react'
import { AutocompleteInput, BooleanInput, ReferenceInput, SelectInput, TextInput, useTranslate } from 'react-admin'
import { useForm, useFormState } from 'react-final-form'
import restClient from 'restClient'
import { useFeatureFlag } from 'util/featureFlags'
import { getRange } from 'util/misc'
import useTranslateParse from 'util/useTranslateParse'
import { required } from 'validations'
import {
  isCalifornianNEM3Utility,
  SECTOR_RESIDENTIAL,
  UTILITY_TARIFF_BILL_FREQUENCY_CHOICES,
  UTILITY_TARIFF_SECTOR_CHOICES,
} from '../common'
import { useStyles } from '../styles'
import { InputWithToolTip } from '../TariffInputs'
import WholesaleTariffFields from './WholesaleTariffFields'

type PropTypes = {
  isCustom?: boolean
  disabled?: boolean
  projectCountry?: string
  isNetworkTariff?: boolean
  isEdit?: boolean
}
const restClientInstance = restClient(window.API_ROOT + '/api')
const GeneralInfoFields: React.FC<PropTypes> = ({ isCustom, disabled, isNetworkTariff, isEdit }) => {
  const form = useForm()
  const classes = useStyles()
  const translate = useTranslate()
  const formState = useFormState()
  const formValues = formState?.values
  const translateParse = useTranslateParse()
  const showWholesaleTariffFields = useFeatureFlag('utility_tariff_wholesale', 'on') ?? false
  const utilityName = formValues.utility_name
  const version = useComponentVersion('text_field')
  const isNem3 =
    formValues.tariffData.solar_compensation_settings.apply_nem3_export_rates ||
    formValues.tariffData.solar_compensation_settings.apply_nem_modified_with_defined_export_rates

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextInput
            fullWidth
            className={`${classes.textInput} ${version === OsTextFieldVersion.V2 ? classes.textInputV2IsRequired : ''}`}
            variant="outlined"
            label="Name"
            source="name"
            required={true}
            validate={required}
            options={{
              InputLabelProps: {
                shrink: true,
              },
            }}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            fullWidth
            className={`${classes.textInput} ${version === OsTextFieldVersion.V2 ? classes.textInputV2IsRequired : ''}`}
            variant="outlined"
            label="Code"
            source="code"
            required={true}
            validate={required}
            options={{
              InputLabelProps: {
                shrink: true,
                required: true,
              },
            }}
            disabled={disabled}
          />
        </Grid>
        {isNetworkTariff && !isEdit && (
          <Grid item xs={12} md={6}>
            <TextInput
              fullWidth
              className={classes.textInput}
              variant="outlined"
              label="Tariff Class"
              source="tariffData.tariff_class"
              options={{
                InputLabelProps: {
                  shrink: true,
                },
              }}
              disabled={disabled}
            />
          </Grid>
        )}
        {!isCustom && (
          <>
            <Grid item xs={12}>
              <TextInput
                fullWidth
                className={classes.textInput}
                variant="outlined"
                label="Code/Name Aliases"
                source="aliases"
                options={{
                  InputLabelProps: {
                    shrink: true,
                  },
                }}
                disabled={disabled}
                endAdornment={'CSV'}
              />
            </Grid>
            {!isNetworkTariff && (
              <Grid item xs={12}>
                <InputWithToolTip
                  interactive
                  message={translateParse(
                    'The energy provider or retailer that sells the electricity. If you cannot find your utility, please let us know by submitting a request <a>here<a>.',
                    {
                      a: (label: string) => (
                        <a target="_blank" rel="noreferrer" href={JIRA_SUPPORT_TICKET_LINK}>
                          {label}
                        </a>
                      ),
                    }
                  )}
                  classNames={'--offset--28'}
                >
                  <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <ReferenceInput
                      fullWidth
                      className={classes.fullWidthInput}
                      classes={{ container: classes.autoComplete }}
                      source="utility"
                      reference="utilities"
                      label="Utility"
                      optionValueField="url"
                      sort={{ field: 'name', order: 'ASC' }}
                      allowEmpty
                      translateChoice={false}
                      onChange={(e) => {
                        if (e) {
                          restClientInstance('CUSTOM_GET', 'custom', {
                            url: e,
                          })
                            .then((res) => {
                              form.mutators.updateField('utility_name', res?.data?.name)
                            })
                            .catch((err) => {
                              console.log('Failed to fetch utility details: ' + err)
                            })
                        } else {
                          form.mutators.updateField('utility_name', null)
                        }
                      }}
                    >
                      <AutocompleteInput
                        fullWidth
                        variant="outlined"
                        optionText="name"
                        optionValue="url"
                        options={{
                          InputLabelProps: {
                            shrink: true,
                          },
                          disabled: disabled,
                          InputProps: {
                            endAdornment: undefined,
                          },
                        }}
                        source={''}
                      />
                    </ReferenceInput>
                  </div>
                </InputWithToolTip>
              </Grid>
            )}
            <DependentInput
              dependsOn="tariffData.wholesale"
              resolve={(wholesale) => Boolean(isNetworkTariff) || (wholesale !== true && wholesale !== 'true')}
            >
              <Grid item xs={12}>
                <InputWithToolTip
                  interactive
                  message={translateParse(
                    'The company that owns the poles and wires to deliver the energy to homes and businesses. Depending on the country/location, often the distributor is also the utility company. If you cannot find your distributor, please let us know by submitting a request <a>here<a>.',
                    {
                      a: (label: string) => (
                        <a target="_blank" rel="noreferrer" href={JIRA_SUPPORT_TICKET_LINK}>
                          {label}
                        </a>
                      ),
                    }
                  )}
                  classNames={'--offset--28'}
                >
                  <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                    <ReferenceInput
                      fullWidth
                      className={classes.fullWidthInput}
                      classes={{ container: classes.autoComplete }}
                      source="distributor"
                      reference="distributors"
                      label="Distributor"
                      optionValueField="url"
                      sort={{ field: 'name', order: 'ASC' }}
                      allowEmpty={!isNetworkTariff}
                      translateChoice={false}
                      {...(isNetworkTariff
                        ? {
                            isRequired: true,
                            validate: required,
                          }
                        : {})}
                    >
                      <AutocompleteInput
                        fullWidth
                        variant="outlined"
                        optionText="name"
                        optionValue="url"
                        required={Boolean(isNetworkTariff)}
                        options={{
                          InputLabelProps: {
                            shrink: true,
                          },
                          disabled: disabled,
                        }}
                        source={''}
                      />
                    </ReferenceInput>
                  </div>
                </InputWithToolTip>
              </Grid>
            </DependentInput>
            <Grid item xs={12} md={6} lg={6}>
              <ReferenceInput
                className={classes.fullWidthInput}
                classes={{ container: classes.autoComplete }}
                isRequired={true}
                validate={required}
                name="country"
                source="country"
                label="Country"
                reference="countries"
                optionValueField="url"
                sort={{ field: 'name', order: 'ASC' }}
                allowEmpty
                onChange={(e) => {
                  if (e) {
                    restClientInstance('CUSTOM_GET', 'custom', {
                      url: e,
                    })
                      .then((res) => {
                        form.mutators.updateField('country_iso2', res?.data?.iso2)
                      })
                      .catch((err) => {
                        console.log('Failed to fetch country details: ' + err)
                      })
                  } else {
                    form.mutators.updateField('country_iso2', null)
                  }
                  form.mutators.updateField('tariffData.solar_compensation_settings', {})
                }}
              >
                <AutocompleteInput
                  required={true}
                  variant="outlined"
                  optionText="name"
                  optionValue="url"
                  options={{
                    InputLabelProps: {
                      shrink: true,
                    },
                    disabled: disabled,
                  }}
                  fullWidth
                  source={''}
                />
              </ReferenceInput>
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <TextInput
                className={classes.textInput}
                variant="outlined"
                disabled={disabled}
                label="Description (Optional)"
                source="tariffData.description"
                fullWidth
                rows={7}
                options={{
                  InputLabelProps: {
                    shrink: true,
                  },
                }}
              />
            </Grid>
          </>
        )}
        <Grid item xs={12} md={6} lg={6}>
          <SelectInput
            className={classes.textInput}
            variant="outlined"
            options={{
              InputLabelProps: {
                shrink: true,
              },
            }}
            disabled={disabled}
            fullWidth
            label="Bill Frequency"
            name="tariffData.bill_frequency"
            source="tariffData.bill_frequency"
            choices={UTILITY_TARIFF_BILL_FREQUENCY_CHOICES}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={6}>
          {!isCustom && !isNetworkTariff && (
            <InputWithToolTip
              message={'Customers on the current tariff will switch to the proposed tariff set here.'}
              classNames={'--offset--28'}
            >
              <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
                <ReferenceInput
                  className={classes.fullWidthInput}
                  classes={{ container: classes.autoComplete }}
                  label="Transition To Proposed Tariff (Optional)"
                  source="transition_to_utility_tariff"
                  name="transition_to_utility_tariff"
                  reference="transition_to_utility_tariffs"
                  optionValueField="url"
                  sort={{ field: 'code', order: 'ASC' }}
                  allowEmpty
                  translateChoice={false}
                >
                  <AutocompleteInput
                    fullWidth
                    variant="outlined"
                    optionText="code"
                    optionValue="url"
                    options={{
                      InputLabelProps: {
                        shrink: true,
                      },
                      disabled: disabled,
                    }}
                    source={''}
                  />
                </ReferenceInput>
              </div>
            </InputWithToolTip>
          )}
        </Grid>
      </Grid>
      <SectionDivider />
      <h2>{translate('Applicability')}</h2>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={6}>
          <SelectInput
            className={classes.textInput}
            variant="outlined"
            options={{
              InputLabelProps: {
                shrink: true,
              },
            }}
            disabled={disabled}
            fullWidth
            name="sector"
            source="sector"
            label="Sector"
            defaultValue={SECTOR_RESIDENTIAL}
            choices={UTILITY_TARIFF_SECTOR_CHOICES}
          />
        </Grid>
        {/*TODO: CHECK IF TRUE -  NEM3 in California has a low-income field, but not in other US States */}
        {isCalifornianNEM3Utility(utilityName) && isNem3 && (
          <DependentInput dependsOn="sector" resolve={(v) => v === SECTOR_RESIDENTIAL}>
            <BooleanInput source="tariffData.applicability.is_low_income" label="Is Low-Income" disabled={disabled} />
          </DependentInput>
        )}
        {!isNetworkTariff && (
          <Grid item xs={12} md={6} lg={6}>
            <SelectInput
              variant="outlined"
              className={classes.textInput}
              source="popularity"
              name="popularity"
              label="Popularity"
              disabled={disabled}
              defaultValue={10}
              options={{
                InputLabelProps: {
                  shrink: true,
                },
              }}
              choices={getRange(101).map((i) => ({
                id: 100 - (i - 1),
                name: 100 - (i - 1) ? 100 - (i - 1) : '0',
              }))}
              translateChoice={false}
              fullWidth={true}
            />
          </Grid>
        )}
      </Grid>
      {!isNetworkTariff && showWholesaleTariffFields && (
        <>
          <SectionDivider />
          <WholesaleTariffFields disabled={disabled} />
          <SectionDivider />
        </>
      )}
    </>
  )
}
export default GeneralInfoFields
